import { useCallback, useEffect, useState } from 'react';
import { fetchAuthenticatedContentfulEntriesCached } from 'Apis/contentfulProxy';
import { useSelector } from 'react-redux';
import { RootState } from 'Rdx/store';
import type { Document as ContentfulDocument } from '@contentful/rich-text-types';

type RichI18nValue = string | ContentfulDocument;
type RichI18nMap = Record<string, RichI18nValue>;

export const useRichI18n = () => {
    const token = useSelector((state: RootState) => state.auth.token);
    const [translations, setTranslations] = useState<RichI18nMap>({});
    const [loading, setLoading] = useState(false);
    const load = useCallback(
        async (preferCache = true) => {
            if (!token) {
                setTranslations({});
                return;
            }
            try {
                setLoading(true);
                const response = await fetchAuthenticatedContentfulEntriesCached(
                    { ['content_type']: 'securityHubRichI18n', limit: 1000 },
                    token,
                    { preferCache, ttlMs: 60 * 60 * 1000 },
                );
                const mappedTranslations = (response?.items ?? []).reduce((acc: RichI18nMap, item: any) => {
                    const key = item?.fields?.key;
                    const value = item?.fields?.value;
                    if (
                        typeof key === 'string' &&
                        (typeof value === 'string' || (value && typeof value === 'object'))
                    ) {
                        acc[key] = value as RichI18nValue;
                    }
                    return acc;
                }, {});
                setTranslations(mappedTranslations);
            } finally {
                setLoading(false);
            }
        },
        [token],
    );
    useEffect(() => {
        void load();
    }, [load]);

    const refresh = useCallback(async () => {
        await load(false);
    }, [load]);

    const t = useCallback(
      (key: string, defaultValue?: RichI18nValue) => translations[key] ?? defaultValue ?? key,
      [translations],
    );

    return {
        t,
        loading,
        refresh,
    };
};
